import { useEffect, useMemo, useRef } from 'react';

import { bindAll, type UnbindFn } from 'bind-event-listener';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import { fg } from '@atlaskit/platform-feature-flags';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import {
	type ClientId,
	getBrowserBridgeAPI,
	type RovoContentBridgeAPI,
} from '@atlassian/rovo-content-bridge-api';
import {
	ContentDestroyCommand,
	ContentReadyCommand,
	getConfluenceEditorClientId,
	ROVO_CHAT_CLIENT_ID,
} from '@atlassian/rovo-content-bridge-api-commands';

import type {
	BuildContentContextFn,
	PassThroughCommandProps,
	StreamAbortCallback,
	StreamCallback,
	StreamEndCallback,
	StreamErrorCallback,
	StreamRepairCallback,
	StreamStartCallback,
	StreamTimeoutCallback,
} from './command-handlers/command-handler';
import { CommandManager } from './command-manager';
import type { AddCommandHandlersFn } from './types/command-manager';
import type { AdditionalContext, ContentLocation } from './types/content';

const bindRovoBridgeTeardown = (teardown: () => void): UnbindFn => {
	const run = () => {
		/**
		 * @description We do NOT want to yield a return value here. It can cause certain unload
		 * handlers to display a window alert with "Are you sure you want to leave this page?".
		 * Specifically, see @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event,
		 * which is remarked to be a cancellable event by browsers.
		 */
		teardown();
		// Although unnecessary (the JS context is being destroyed), we remove the event listeners
		// to avoid any potential memory leaks due to hanging references - side effects can cause this.
		unbind();
	};

	// Below, we attempt to teardown on different event handlers which we know will trigger
	// on complete unmount of the document in this JS context. Note: Same-origin iframes are
	// indeed considered their own JS context here; just that they have access to other contexts.
	// Browsers report `unload` and `beforeunload` to be unreliable. Hence, we leverage a chain
	// of these events. See official MDN documentation for more (linked below).

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
	 * TODO - bring beforeunload back by consolidating handlers per discussion at -
	 * https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/207747/overview#comment-674441389
	 */
	// window.addEventListener('beforeunload', run, { once: true });

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event
	 */
	const unbind = bindAll(window, [
		{
			type: 'unload',
			listener: run,
			options: { once: true },
		},
		{
			type: 'pagehide',
			listener: run,
			options: { once: true },
		},
	]);

	return unbind;
};

function useRovoBridgeLifecycle(
	props: UseEditorRovoBridgeProps & {
		clientId: ClientId;
		rovoBridgeAPI: RovoContentBridgeAPI | null;
	},
) {
	const contentType = 'page';
	const { clientId, contentLocation, contentId, rovoBridgeAPI } = props;

	useEffect(() => {
		if (!rovoBridgeAPI) {
			return;
		}

		rovoBridgeAPI.ready(clientId);
		rovoBridgeAPI.send(ContentReadyCommand, {
			senderId: clientId,
			receiverId: ROVO_CHAT_CLIENT_ID,
			contentId,
			contentType,
			canProvideContext: true,
			contentContext: contentLocation,
		});

		function rovoBridgeTeardown() {
			if (!rovoBridgeAPI) {
				return;
			}

			rovoBridgeAPI.send(ContentDestroyCommand, {
				senderId: clientId,
				receiverId: ROVO_CHAT_CLIENT_ID,
				contentId,
				contentType,
				contentContext: contentLocation,
			});
			rovoBridgeAPI.teardown(clientId);
		}

		const unbind = bindRovoBridgeTeardown(() => rovoBridgeTeardown());

		return () => {
			try {
				unbind();
				rovoBridgeTeardown();
				// TODO: EDITOR-123 - clear inactivity timeouts?
			} catch {
				// TODO: EDITOR-123 - handle errors; should go to Sentry
				// could be sent in future by navigation lockout
			}
		};
	}, [clientId, contentLocation, contentId, contentType, rovoBridgeAPI]);
}

export type UseEditorRovoBridgeProps = {
	addCommandHandlers?: AddCommandHandlersFn;
	additionalContext?: AdditionalContext;
	buildContentContext?: BuildContentContextFn;
	contentId: string;
	contentLocation: ContentLocation;
	editorAPI:
		| PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin, AIPlugin]>
		| undefined
		| null;
	onStream?: StreamCallback;
	onStreamAbort?: StreamAbortCallback;
	onStreamEnd?: StreamEndCallback;
	onStreamError?: StreamErrorCallback;
	onStreamRepair?: StreamRepairCallback;
	onStreamStart?: StreamStartCallback;
	onStreamTimeout?: StreamTimeoutCallback;
	useGenericEditorSkill?: boolean;
};

/**
 * React hook that manages the Rovo bridge connection for AI streaming functionality in the Atlassian Editor.
 *
 * This hook establishes a bidirectional communication bridge between the editor and Rovo chat,
 * enabling AI-powered content generation, editing, and streaming operations. It handles the complete
 * lifecycle of the bridge connection including setup, command registration, and cleanup.
 *
 * Typically this is required for use with the `aiStreamingOrchestratorPlugin` and the free gen prompt field
 *
 * @param props - Configuration properties for the Rovo bridge
 * @param props.contentLocation - The location context of the content ('global' | 'staging-area')
 * @param props.contentId - Unique identifier for the content being edited
 * @param props.editorAPI - The editor's plugin API, must include AnalyticsPlugin and AiStreamingOrchestratorPlugin
 * @param props.useGenericEditorSkill - [Recommended] Optional to enable the generic editor skill for products that don't have a specific minion
 * @param props.additionalContext - Optional additional context to pass to ReadContentCommand
 * @param props.buildContentContext - Optional function to build content context for AI operations
 * @param props.onStreamStart - Optional callback when AI streaming starts
 * @param props.onStream - Optional callback for each streaming chunk received
 * @param props.onStreamEnd - Optional callback when AI streaming completes successfully
 * @param props.onStreamAbort - Optional callback when AI streaming is aborted
 * @param props.onStreamTimeout - Optional callback when AI streaming times out
 * @param props.onStreamError - Optional callback when AI streaming encounters an error
 * @param props.onStreamRepair - Optional callback for AI stream repair operations
 * @param props.addCommandHandlers - Optional function to add custom command handlers
 *
 * @example
 * ```tsx
 * import { useEditorRovoBridge } from '@atlassian/editor-rovo-bridge';
 *
 * function MyEditor() {
 *   const { preset, editorApi: editorAPI } = usePreset(...);
 *
 *   useEditorRovoBridge({
 *     contentLocation: 'global',
 *     contentId: 'page-123',
 *     editorAPI,
 *     useGenericEditorSkill: true,
 *     additionalContext: {
 *       isViewMode: true,
 *     },
 *     onStreamStart: ({ streamType, payload }) => {
 *       console.log('AI streaming started:', streamType);
 *     },
 *     onStream: ({ streamType, payload }) => {
 *       console.log('AI streaming data:', payload);
 *     },
 *     onStreamEnd: ({ streamType, adfDoc }) => {
 *       console.log('AI streaming completed with ADF:', adfDoc);
 *     },
 *     onStreamError: ({ error, streamType }) => {
 *       console.error('AI streaming error:', error);
 *     },
 *   });
 *
 *   return <ComposableEditor preset={preset} />;
 * }
 * ```
 */
export const useEditorRovoBridge = ({
	contentLocation,
	contentId,
	editorAPI,
	useGenericEditorSkill,
	additionalContext,
	buildContentContext,
	onStreamStart,
	onStream,
	onStreamEnd,
	onStreamAbort,
	onStreamTimeout,
	onStreamError,
	onStreamRepair,
	addCommandHandlers,
}: UseEditorRovoBridgeProps) => {
	const contentType = 'page';
	const clientId = getConfluenceEditorClientId(contentId);

	useEffect(() => {
		if (!editorAPI) {
			return;
		}
		editorAPI.aiExperience?.actions.updateTargetId(contentId);
	}, [editorAPI, contentId]);

	const rovoBridgeAPI = useMemo(() => {
		if (!editorAPI) {
			return null;
		}

		return getBrowserBridgeAPI();
	}, [editorAPI]);

	const commandProps = useMemo<PassThroughCommandProps | null>(() => {
		if (!editorAPI) {
			return null;
		}
		return {
			clientId,
			contentLocation,
			contentId,
			contentType,
			editorAPI,
			useGenericEditorSkill,
			additionalContext,
			buildContentContext,
			onStreamStart,
			onStream,
			onStreamEnd,
			onStreamAbort,
			onStreamTimeout,
			onStreamError,
			onStreamRepair,
		};
	}, [
		clientId,
		contentLocation,
		contentId,
		contentType,
		editorAPI,
		useGenericEditorSkill,
		additionalContext,
		buildContentContext,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamAbort,
		onStreamTimeout,
		onStreamError,
		onStreamRepair,
	]);

	useEffect(() => {
		if (fg('platform_editor_aifc_rovo_bridge_deps_update')) {
			return;
		}
		if (!editorAPI || !commandProps || !rovoBridgeAPI) {
			return;
		}
		const commandManager = new CommandManager(clientId, rovoBridgeAPI, commandProps);

		if (addCommandHandlers) {
			addCommandHandlers({ commandManager });
		} else {
			commandManager.addDefaultCommandHandlers();
		}

		commandManager.registerCommands();
		return () => {
			commandManager.deregisterCommands();
		};
	}, [
		clientId,
		contentLocation,
		rovoBridgeAPI,
		commandProps,
		editorAPI,
		buildContentContext,
		addCommandHandlers,
	]);

	const commandManagerRef = useRef<CommandManager | null>(null);

	useEffect(() => {
		if (!editorAPI || !rovoBridgeAPI || !commandProps) {
			return;
		}

		// only initialize command manager once because
		// createCommandProcessor is stateful and doesn't support re-creation for now
		if (!commandManagerRef.current && fg('platform_editor_aifc_rovo_bridge_deps_update')) {
			commandManagerRef.current = new CommandManager(
				clientId,
				rovoBridgeAPI,
				commandProps,
				addCommandHandlers,
			);
			commandManagerRef.current.addAndRegisterCommands();
		}
	}, [editorAPI, clientId, rovoBridgeAPI, commandProps, addCommandHandlers]);

	// clientId shouldn't change, but just in case
	// when it changes, we re-register the commands to the updated clientId
	useEffect(() => {
		if (!commandManagerRef.current || !rovoBridgeAPI) {
			return;
		}
		commandManagerRef.current.updateClientId(clientId, rovoBridgeAPI);
	}, [clientId, rovoBridgeAPI]);

	// when commandProps changes, we update the command props
	useEffect(() => {
		if (!commandManagerRef.current || !commandProps) {
			return;
		}
		commandManagerRef.current.updateCommandProps(commandProps);
	}, [commandProps]);

	// when addCommandHandlers changes, we deregister and register the commands
	useEffect(() => {
		if (!commandManagerRef.current) {
			return;
		}

		commandManagerRef.current.updateAddCommandHandlers(addCommandHandlers);
	}, [addCommandHandlers]);

	// when the component unmounts, we deregister the commands
	useEffect(() => {
		return () => {
			commandManagerRef.current?.deregisterCommands();
		};
	}, []);

	useRovoBridgeLifecycle({
		contentLocation,
		contentId,
		editorAPI,
		buildContentContext,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamAbort,
		onStreamTimeout,
		onStreamError,
		onStreamRepair,
		addCommandHandlers,
		clientId,
		rovoBridgeAPI,
	});
};
